import { useEventStore } from "@/store/event/eventStore";
import { Field, Formik, Form } from "formik";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { Label, Input, Col, Row, FormGroup, FormFeedback } from "reactstrap";
import UserContext from "store/guest_user/userContext";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

// Dictionary mapping event IDs to their required form fields
// To add new events, simply add a new entry with the event ID as key
const EVENT_FORM_FIELDS = {
  // Events 167075 require state
  167075: {
    fields: [
      {
        name: "state",
        label: "State",
        placeholder: "Enter State",
        required: true,
      },
    ],
  },
};

// Helper function to get event fields configuration
const getEventFields = () => {
  const { eventId } = useEventStore();
  return EVENT_FORM_FIELDS[eventId] || { fields: [] };
};

const CompleteProfile = () => {
  const { t } = useTranslation();
  const { isUserLoading, handleOnUpdateProfile, user } =
    useContext(UserContext);
  const location = useLocation();
  const eventId = useMemo(() => {
    try {
      const c = location.pathname.split("/");
      return Number(c[c.length - 1]);
    } catch (err) {
      console.error(err);
      return -1;
    }
  }, [location]);

  const handleSubmit = (values) => {
    const userProfile = { ...values };
    handleOnUpdateProfile(userProfile);
  };

  // Get current event fields configuration
  const currentEventFields = getEventFields(eventId);

  // Create dynamic validation schema based on event fields
  const createValidationSchema = () => {
    const schema = {
      name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("First Name is Required"),
      tnc: Yup.boolean().oneOf([true], "You must accept the terms to continue"),
    };

    // Add validation for event-specific fields
    currentEventFields.fields.forEach((field) => {
      if (field.required) {
        schema[field.name] = Yup.string().required(
          `${field.label} is Required`
        );
      } else {
        schema[field.name] = Yup.string();
      }
    });

    return Yup.object().shape(schema);
  };

  const ProfileSchema = createValidationSchema();

  const [defaultData, setDefaultData] = useState({});
  useEffect(() => {
    let registeredData = localStorage.getItem("registration-data");
    if (registeredData) {
      registeredData = JSON.parse(registeredData);
      setDefaultData({ fullName: registeredData.name });
    }
  }, []);

  return (
    <>
      <div className="w-100 text-center complete-profile">
        <div>
          <strong className=" ">{t("Complete your Profile")}</strong>
        </div>
        {!isUserLoading && (
          <div className="mt-3">
            <Formik
              validationSchema={ProfileSchema}
              initialValues={{
                mobile_number: user.phoneNumber || user.mobile_number,
                name: user.displayName || user.name,
                email: user.email,
                tnc: false,
                ...defaultData,
                // Initialize event-specific fields
                ...currentEventFields.fields.reduce((acc, field) => {
                  acc[field.name] = user[field.name] || "";
                  return acc;
                }, {}),
              }}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <div>
                  <Form>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <div className="text-start ms-1">
                            <small className="form-label  ">{t("Name")}*</small>
                          </div>

                          <Field
                            placeholder={t("Full Name")}
                            as={Input}
                            name="name"
                            className={"me-2"}
                            invalid={errors.name}
                          />
                          {errors.name && touched.name ? (
                            <FormFeedback className="text-start">
                              {errors.name}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                    {/* <Row className="mt-3">
                    <Col>
                      <Field
                        placeholder="Email Id"
                        as={Input}
                        name="email"
                        className={"me-2"}
                        invalid={errors.email}
                      />
                      {errors.email && touched.email ? (
                        <FormFeedback className="text-start">
                          {errors.email}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row> */}
                    <Row className="mt-3">
                      <Col>
                        <div>
                          <div className="text-start ms-1">
                            <small className="form-label  ">
                              {t("Mobile Number")}*
                            </small>
                          </div>
                          <Field
                            as={Input}
                            name="mobile_number"
                            className={"me-2"}
                            disabled="true"
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Dynamic event-specific fields */}
                    {currentEventFields.fields.map((field, index) => (
                      <Row key={field.name} className="mt-3">
                        <Col>
                          <div>
                            <div className="text-start ms-1">
                              <small className="form-label">
                                {field.label}
                                {field.required ? "*" : ""}
                              </small>
                            </div>
                            <Field
                              as={Input}
                              name={field.name}
                              className={"me-2"}
                              placeholder={field.placeholder}
                              invalid={
                                errors[field.name] && touched[field.name]
                              }
                            />
                            {errors[field.name] && touched[field.name] ? (
                              <FormFeedback className="text-start">
                                {errors[field.name]}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                    ))}

                    {/* <Row className="mt-3">
                    <Col>
                      <div>
                        <div className="text-start ms-1">
                          <small className="form-label  ">{t("Instagram ID")}*</small>
                        </div>
                        <Field
                          as={Input}
                          name="instagramId"
                          className={"me-2"}
                          placeholder={t("Instagram ID")}
                        />
                      </div>
                    </Col>
                  </Row> */}
                    <div className="mt-3 d-flex justify-content-start">
                      <FormGroup check>
                        <Field
                          as={Input}
                          type="checkbox"
                          name="tnc"
                          className={"me-2"}
                          invalid={errors.tnc}
                        />
                        <Label check>
                          {t("I have read and I agree to the Privacy Policy")}{" "}
                          <a
                            href="https://fotoowl.ai/privacypolicy/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t("Privacy Policy")}
                          </a>
                        </Label>
                        {errors.tnc && touched.tnc ? (
                          <FormFeedback className="text-start">
                            {errors.tnc}
                          </FormFeedback>
                        ) : null}
                      </FormGroup>
                    </div>
                    <div className="mt-4 w-100">
                      <button
                        type="submit"
                        className="btn btn-fotoowl btn-rounded-2 w-100"
                        disabled={isSubmitting}
                      >
                        <strong>{t("Create My Profile")}</strong>
                      </button>
                    </div>
                  </Form>
                </div>
              )}
            </Formik>
          </div>
        )}
      </div>
    </>
  );
};

export default CompleteProfile;
